<template>
  <div class="fence-management-page">
    <!-- Header com Título e Ações Principais -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon icon="ShieldIcon" size="24" class="mr-50" />
          {{ $t('Gestão de Cercas Virtuais') }}
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Monitore, crie e gerencie limites geográficos e alertas de entrada, saída e permanência.') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-md-0" style="gap: 8px;">
        <!-- Alternador de Modo de Visualização -->
        <div class="view-mode-toggle btn-group btn-group-sm">
          <b-button
            :variant="viewMode === 'map' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'map'"
            class="d-inline-flex align-items-center"
          >
            <feather-icon icon="MapIcon" size="14" class="mr-50" />
            <span>{{ $t('Mapa') }}</span>
          </b-button>
          <b-button
            :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'table'"
            class="d-inline-flex align-items-center"
          >
            <feather-icon icon="ListIcon" size="14" class="mr-50" />
            <span>{{ $t('Tabela') }}</span>
          </b-button>
        </div>

        <b-button
          variant="outline-info"
          size="sm"
          :to="{ name: 'fence-tutorial' }"
          class="d-inline-flex align-items-center"
          v-b-tooltip.hover
          :title="$t('Aprenda a gerenciar suas cercas')"
        >
          <feather-icon icon="HelpCircleIcon" size="14" class="mr-50" />
          <span>{{ $t('Tutorial') }}</span>
        </b-button>

        <b-button
          variant="outline-secondary"
          size="sm"
          :to="{ name: 'fence-report' }"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
          <span>{{ $t('Report') }}</span>
        </b-button>

        <b-button
          variant="primary"
          size="sm"
          @click="newVirtualFence"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          <span>{{ $t('Criar Nova Cerca') }}</span>
        </b-button>
      </div>
    </div>



    <!-- Painel de Filtros Avançados -->
    <transition name="slide-fade">
      <b-card v-if="showFilters" no-body class="filters-card shadow-sm mb-1">
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon icon="FilterIcon" size="16" class="text-primary mr-50" />
            <h6 class="mb-0 font-weight-bolder">{{ $t('Filtros de Pesquisa') }}</h6>
            <b-badge v-if="hasActiveFilters" variant="light-primary" pill class="ml-50">
              {{ $t('Ativos') }}
            </b-badge>
          </div>

          <div class="d-flex align-items-center" style="gap: 6px;">
            <b-button
              v-if="hasActiveFilters"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="clearFilters"
              v-b-tooltip.hover
              :title="$t('Limpar Filtros')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="$t('Fechar Filtros')"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="py-1 px-1">
          <b-row class="align-items-end">
            <!-- Campo: Nome da Cerca -->
            <b-col cols="12" md="4" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Nome da Cerca') }}</label>
              <b-input-group size="sm">
                <b-form-input
                  v-model="searchQueryFence"
                  :placeholder="$t('Ex: Galpão, Pátio, Fazenda...')"
                  @keyup.enter="handleSearch"
                />
                <b-input-group-append v-if="searchQueryFence">
                  <b-button variant="outline-secondary" @click="searchQueryFence = ''; handleSearch()">
                    <feather-icon icon="XIcon" size="12" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>

            <!-- Campo: Cliente -->
            <b-col cols="12" md="4" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Cliente') }}</label>
              <vue-autosuggest
                v-model="searchQueryCustomer"
                :suggestions="filteredOptionsCustomer"
                :input-props="inputPropsCustomer"
                @selected="selectHandlerCustomer"
                @input="onInputChangeCustomer"
              >
                <template slot-scope="{ suggestion }">
                  <div class="p-50 border-bottom">
                    <span class="font-weight-bold d-block">{{ suggestion.item.name }}</span>
                    <small v-if="suggestion.item.ownerName" class="text-muted">
                      {{ $t('Pertence a: ') }} {{ suggestion.item.ownerName }}
                    </small>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Switch: Incluir Subclientes + Botões de Ação -->
            <b-col cols="12" md="4" class="d-flex align-items-center justify-content-between">
              <div class="custom-control custom-switch custom-control-primary">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="includeSubcustomersSwitch"
                  v-model="includeSubcustomers"
                >
                <label class="custom-control-label font-small-2 font-weight-bold text-muted cursor-pointer" for="includeSubcustomersSwitch">
                  {{ $t('Incluir subclientes') }}
                </label>
              </div>

              <b-button variant="primary" size="sm" @click="handleSearch" class="d-inline-flex align-items-center">
                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                {{ $t('Search') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- ===================== MODO 1: VISUALIZAÇÃO EM MAPA ===================== -->
    <div v-show="viewMode === 'map'" class="map-view-wrapper position-relative">
      <!-- Barra Flutuante de Ferramentas sobre o Mapa -->
      <div class="map-floating-toolbar">
        <div class="d-flex align-items-center" style="gap: 8px;">
          <!-- Botão Toggle de Filtros -->
          <b-button
            size="sm"
            :variant="hasActiveFilters ? 'primary' : 'light'"
            class="shadow-sm d-inline-flex align-items-center"
            @click="showFilters = !showFilters"
            v-b-tooltip.hover
            :title="$t('Filtrar Cercas')"
          >
            <feather-icon icon="FilterIcon" size="14" class="mr-25" />
            <span class="d-none d-sm-inline">{{ $t('Filters') }}</span>
            <b-badge v-if="hasActiveFilters" variant="warning" pill class="ml-25 font-small-1">
              !
            </b-badge>
          </b-button>

          <!-- Botão Toggle da Gaveta Lateral de Cercas -->
          <b-button
            size="sm"
            :variant="showSidebar ? 'primary' : 'light'"
            class="shadow-sm d-inline-flex align-items-center"
            @click="showSidebar = !showSidebar"
            v-b-tooltip.hover
            :title="showSidebar ? $t('Ocultar Lista de Cercas') : $t('Ver Lista de Cercas')"
          >
            <feather-icon icon="SidebarIcon" size="14" class="mr-25" />
            <span class="d-none d-sm-inline">{{ $t('Cercas') }} ({{ allFences.length }})</span>
          </b-button>

          <!-- Botão Centralizar Todas -->
          <b-button
            size="sm"
            variant="light"
            class="shadow-sm d-inline-flex align-items-center"
            @click="fitAllFences"
            v-b-tooltip.hover
            :title="$t('Ajustar Zoom para todas as cercas')"
          >
            <feather-icon icon="Maximize2Icon" size="14" />
          </b-button>

          <!-- Controle de Camada: Exibir/Ocultar Ativos no Mapa (UI/UX Refinada) -->
          <b-button-group size="sm" class="asset-layer-control shadow-sm">
            <b-button
              size="sm"
              :variant="showAssetsOnMap ? 'primary' : 'light'"
              class="d-inline-flex align-items-center font-weight-bolder px-75"
              :disabled="isLoadingAssets"
              @click="toggleShowAssets"
              v-b-tooltip.hover
              :title="showAssetsOnMap ? $t('Ocultar veículos do mapa') : $t('Buscar e exibir veículos no mapa')"
            >
              <b-spinner v-if="isLoadingAssets" small class="mr-50" />
              <feather-icon v-else icon="TruckIcon" size="14" class="mr-50" />

              <span class="d-none d-md-inline mr-25">{{ $t('Ativos') }}</span>

              <!-- Badge com status ou contagem -->
              <transition name="fade">
                <b-badge
                  v-if="showAssetsOnMap && !isLoadingAssets"
                  variant="light-primary"
                  pill
                  class="ml-25 font-small-1 px-50 py-25 badge-asset-count"
                >
                  {{ assetsWithCoordinates.length }}
                </b-badge>
                <b-badge
                  v-else-if="!showAssetsOnMap && !isLoadingAssets"
                  variant="light-secondary"
                  pill
                  class="ml-25 font-small-1 px-50 py-25 badge-asset-off"
                >
                  {{ $t('Off') }}
                </b-badge>
              </transition>
            </b-button>

            <!-- Ação rápida para atualizar os veículos mais recentes se a camada estiver ativa -->
            <b-button
              v-if="showAssetsOnMap"
              size="sm"
              variant="primary"
              class="px-50 d-inline-flex align-items-center border-left-light"
              :disabled="isLoadingAssets"
              @click="refreshAssets"
              v-b-tooltip.hover
              :title="$t('Atualizar posições mais recentes dos veículos')"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="13"
                :class="{ 'feather-spin': isLoadingAssets }"
              />
            </b-button>
          </b-button-group>
        </div>

        <!-- Busca de Endereço / Coordenadas -->
        <div class="location-search-box shadow-sm">
          <b-input-group size="sm">
            <b-form-input
              v-model="locationQuery"
              :placeholder="$t('Buscar endereço ou lat,lng...')"
              @keyup.enter="searchLocation"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="searchLocation" :disabled="isSearchingLocation">
                <feather-icon v-if="!isSearchingLocation" icon="SearchIcon" size="14" />
                <b-spinner v-else small />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <!-- Gaveta Lateral Flutuante: Lista Interativa de Cercas -->
      <transition name="slide-right">
        <div v-if="showSidebar" class="fence-sidebar-drawer shadow-lg">
          <div class="drawer-header d-flex justify-content-between align-items-center p-1 border-bottom bg-white">
            <div class="d-flex align-items-center">
              <feather-icon icon="LayersIcon" size="16" class="text-primary mr-50" />
              <h6 class="mb-0 font-weight-bolder">{{ $t('Cercas no Mapa') }}</h6>
              <b-badge variant="light-primary" pill class="ml-50 font-small-1">
                {{ allFences.length }}
              </b-badge>
            </div>
            <b-button variant="flat-secondary" size="sm" class="btn-icon p-25" @click="showSidebar = false">
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>

          <!-- Lista Rolável de Cards -->
          <div class="drawer-content p-75">
            <div v-if="isLoadingFences" class="text-center py-3 text-muted">
              <b-spinner small variant="primary" class="mr-50" />
              <span>{{ $t('Carregando cercas...') }}</span>
            </div>

            <div v-else-if="allFences.length === 0" class="text-center py-3 text-muted">
              <feather-icon icon="InboxIcon" size="32" class="mb-50 text-muted opacity-50" />
              <p class="font-small-3 mb-0">{{ $t('Nenhuma cerca encontrada.') }}</p>
            </div>

            <div
              v-else
              v-for="fence in paginatedSidebarFences"
              :key="fence.id"
              class="fence-item-card mb-75 p-75 rounded cursor-pointer transition-all"
              :class="{ 'fence-item-card--active': selectedFenceId === fence.id }"
              @click="focusFence(fence)"
            >
              <div class="d-flex justify-content-between align-items-start mb-25">
                <h6 class="font-weight-bolder mb-0 text-truncate fence-card-title" :title="fence.name">
                  {{ fence.name }}
                </h6>
                <b-badge
                  v-if="fence.virtualFenceTypeName"
                  variant="light-info"
                  class="font-small-1 text-truncate"
                  style="max-width: 110px;"
                >
                  {{ fence.virtualFenceTypeName }}
                </b-badge>
              </div>

              <small v-if="fence.customerName" class="text-muted d-block text-truncate mb-50">
                <feather-icon icon="BriefcaseIcon" size="11" class="mr-25" />
                {{ fence.customerName }}
              </small>

              <!-- Badges de Alertas -->
              <div class="d-flex flex-wrap align-items-center mb-50" style="gap: 4px;">
                <span
                  class="fence-alert-chip d-flex align-items-center justify-content-center"
                  :class="fence.alertIn ? 'fence-alert-chip--active' : 'fence-alert-chip--inactive'"
                  v-b-tooltip.hover
                  :title="$t('Alerta de Entrada')"
                >
                  <feather-icon icon="LogInIcon" size="12" />
                </span>
                <span
                  class="fence-alert-chip d-flex align-items-center justify-content-center"
                  :class="fence.alertOut ? 'fence-alert-chip--active' : 'fence-alert-chip--inactive'"
                  v-b-tooltip.hover
                  :title="$t('Alerta de Saída')"
                >
                  <feather-icon icon="LogOutIcon" size="12" />
                </span>
                <span
                  class="fence-alert-chip d-flex align-items-center justify-content-center"
                  :class="fence.alertStayIn ? 'fence-alert-chip--active' : 'fence-alert-chip--inactive'"
                  v-b-tooltip.hover
                  :title="$t('Alerta Permanece Dentro')"
                >
                  <feather-icon icon="Minimize2Icon" size="12" />
                </span>
                <span
                  class="fence-alert-chip d-flex align-items-center justify-content-center"
                  :class="fence.alertStayOut ? 'fence-alert-chip--active' : 'fence-alert-chip--inactive'"
                  v-b-tooltip.hover
                  :title="$t('Alerta Permanece Fora')"
                >
                  <feather-icon icon="Maximize2Icon" size="12" />
                </span>
              </div>

              <!-- Ações Rápidas do Card -->
              <div class="d-flex justify-content-end align-items-center pt-25 border-top" style="gap: 6px;">
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="p-25 d-inline-flex align-items-center font-small-1"
                  @click.stop="focusFence(fence)"
                  v-b-tooltip.hover
                  :title="$t('Focar no Mapa')"
                >
                  <feather-icon icon="CrosshairIcon" size="13" class="mr-25" />
                  {{ $t('Focar') }}
                </b-button>
                <b-button
                  variant="flat-info"
                  size="sm"
                  class="p-25 d-inline-flex align-items-center font-small-1"
                  @click.stop="editFence(fence.id)"
                  v-b-tooltip.hover
                  :title="$t('Editar')"
                >
                  <feather-icon icon="Edit2Icon" size="13" />
                </b-button>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="p-25 d-inline-flex align-items-center font-small-1"
                  @click.stop="confirmDelete(fence.id)"
                  v-b-tooltip.hover
                  :title="$t('Delete')"
                >
                  <feather-icon icon="Trash2Icon" size="13" />
                </b-button>
              </div>
            </div>
          </div>

          <!-- Paginação da Sidebar (Map View) -->
          <div v-if="allFences.length > 0" class="drawer-footer p-50 border-top bg-white d-flex flex-column align-items-center">
            <b-pagination
              v-model="sidebarCurrentPage"
              :total-rows="allFences.length"
              :per-page="sidebarPerPage"
              size="sm"
              first-number
              last-number
              class="mb-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="14" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="14" />
              </template>
            </b-pagination>
            <small class="text-muted mt-25">
              {{ (sidebarCurrentPage - 1) * sidebarPerPage + (paginatedSidebarFences.length ? 1 : 0) }} - {{ Math.min(sidebarCurrentPage * sidebarPerPage, allFences.length) }} {{ $t('de') }} {{ allFences.length }}
            </small>
          </div>
        </div>
      </transition>

      <!-- Container do Mapa Leaflet -->
      <div id="map-fence" class="map-container rounded shadow-sm overflow-hidden">
        <l-map ref="mapfence" class="map-canvas" :zoom="zoom" :center="center" :max-zoom="18">
          <!-- Controle de Camadas -->
          <l-control-layers position="topright" />

          <!-- Camada Base: OpenStreetMap -->
          <l-tile-layer
            :name="$t('Mapa Padrão')"
            :url="'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'"
            layer-type="base"
            checked
          />

          <!-- Camada Base: ArcGIS Satélite -->
          <l-tile-layer
            :name="$t('Satélite')"
            :url="arcgisSatelliteUrl"
            layer-type="base"
          />

          <!-- Overlays do ArcGIS -->
          <l-tile-layer
            :name="$t('Nomes de Cidades/Locais')"
            :url="arcgisLabelsUrl"
            layer-type="overlay"
          />
          <l-tile-layer
            :name="$t('Nomes de Ruas')"
            :url="arcgisRoadsUrl"
            layer-type="overlay"
          />

          <!-- Cercas em Polígono -->
          <l-polygon
            v-for="polygon in polygonFences"
            :key="'poly-' + polygon.id"
            :lat-lngs="polygon.points"
            :color="'#7367f0'"
            :fill-color="'#7367f0'"
            :fill-opacity="0.2"
            :weight="2"
          >
            <l-popup>
              <popup-fence
                :is-fence="true"
                :my-id="polygon.id"
                :name="polygon.name"
                :max-speed="polygon.maxSpeed"
                :alert-in="polygon.alertIn"
                :alert-out="polygon.alertOut"
                :alert-stay-in="polygon.alertStayIn"
                :alert-stay-out="polygon.alertStayOut"
                :virtual-fence-type-name="polygon.virtualFenceTypeName"
                @event-to-fence="editFence(polygon.id)"
                @event-delete-fence="confirmDelete(polygon.id)"
              />
            </l-popup>
          </l-polygon>

          <!-- Cercas em Círculo -->
          <l-circle
            v-for="circle in circleFences"
            :key="'circ-' + circle.id"
            :lat-lng="[circle.lat, circle.lng]"
            :radius="circle.radius"
            :color="'#28c76f'"
            :fill-color="'#28c76f'"
            :fill-opacity="0.2"
            :weight="2"
          >
            <l-popup>
              <popup-fence
                :is-fence="true"
                :my-id="circle.id"
                :name="circle.name"
                :max-speed="circle.maxSpeed"
                :alert-in="circle.alertIn"
                :alert-out="circle.alertOut"
                :alert-stay-in="circle.alertStayIn"
                :alert-stay-out="circle.alertStayOut"
                :virtual-fence-type-name="circle.virtualFenceTypeName"
                @event-to-fence="editFence(circle.id)"
                @event-delete-fence="confirmDelete(circle.id)"
              />
            </l-popup>
          </l-circle>

          <!-- Marcadores de Veículos/Ativos (MarkerCluster) -->
          <marker-cluster v-if="showAssetsOnMap && assetsWithCoordinates && assetsWithCoordinates.length > 0">
            <l-marker
              v-for="asset in assetsWithCoordinates"
              :key="asset.id"
              :lat-lng="[asset.lastLat, asset.lastLng]"
            >
              <l-icon
                :class-name="`someExtraClass asset-${asset.lastWorkstatus}`"
                :icon-size="dynamicSize"
                :tooltip-anchor="dynamicTooltip"
                :popup-anchor="dynamicPopup"
                :icon-anchor="dynamicAnchor"
              >
                <div>
                  <img class="with-icon" :src="resolveIcon(asset.assetType, asset.iconUrl)" />
                </div>
              </l-icon>
              <l-popup>
                <div class="p-25 font-weight-bold">{{ asset.plate }}</div>
              </l-popup>
            </l-marker>
          </marker-cluster>
        </l-map>
      </div>
    </div>

    <!-- ===================== MODO 2: VISUALIZAÇÃO EM TABELA ===================== -->
    <b-card v-show="viewMode === 'table'" no-body class="table-view-card shadow-sm">
      <!-- Toolbar da Tabela -->
      <div class="p-1 d-flex flex-column flex-sm-row justify-content-between align-items-center border-bottom" style="gap: 8px;">
        <div class="d-flex align-items-center">
          <feather-icon icon="LayersIcon" size="18" class="text-primary mr-50" />
          <h5 class="mb-0 font-weight-bolder">{{ $t('Listagem de Cercas') }}</h5>
          <b-badge variant="light-primary" pill class="ml-75 font-small-1">
            {{ totalFencesCount }} {{ $t('registros') }}
          </b-badge>
        </div>

        <div class="d-flex align-items-center" style="gap: 8px;">
          <!-- Seletor por página -->
          <div class="d-flex align-items-center">
            <small class="text-muted mr-50 font-weight-bold">{{ $t('Show') }}</small>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width: 75px;"
              @change="currentPage = 1"
            />
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            class="d-inline-flex align-items-center"
            @click="showFilters = !showFilters"
          >
            <feather-icon icon="FilterIcon" size="14" class="mr-50" />
            <span>{{ $t('Filtros') }}</span>
          </b-button>
        </div>
      </div>

      <!-- Tabela BootstrapVue -->
      <b-table
        :items="allFences"
        :fields="tableFields"
        :per-page="perPage"
        :current-page="currentPage"
        responsive
        show-empty
        :empty-text="$t('Nenhuma cerca encontrada.')"
        :busy="isLoadingFences"
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center text-primary py-3">
            <b-spinner class="align-middle mr-50" />
            <strong>{{ $t('Carregando cercas...') }}</strong>
          </div>
        </template>

        <!-- Coluna: Nome -->
        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <div class="fence-table-icon-wrapper mr-75">
              <feather-icon icon="ShieldIcon" size="16" class="text-primary" />
            </div>
            <div>
              <span class="font-weight-bolder text-primary d-block">{{ data.item.name }}</span>
              <small v-if="data.item.customerName" class="text-muted">
                {{ data.item.customerName }}
              </small>
            </div>
          </div>
        </template>

        <!-- Coluna: Tipo -->
        <template #cell(virtualFenceTypeName)="data">
          <b-badge v-if="data.item.virtualFenceTypeName" variant="light-info" class="font-small-1">
            {{ data.item.virtualFenceTypeName }}
          </b-badge>
          <span v-else class="text-muted font-small-2">—</span>
        </template>

        <!-- Coluna: Alerta Entrada (In) -->
        <template #cell(alertIn)="data">
          <b-badge :variant="data.item.alertIn ? 'light-success' : 'light-secondary'" class="font-small-1">
            <feather-icon :icon="data.item.alertIn ? 'CheckIcon' : 'XIcon'" size="10" class="mr-25" />
            {{ data.item.alertIn ? $t('Sim') : $t('Não') }}
          </b-badge>
        </template>

        <!-- Coluna: Alerta Saída (Out) -->
        <template #cell(alertOut)="data">
          <b-badge :variant="data.item.alertOut ? 'light-success' : 'light-secondary'" class="font-small-1">
            <feather-icon :icon="data.item.alertOut ? 'CheckIcon' : 'XIcon'" size="10" class="mr-25" />
            {{ data.item.alertOut ? $t('Sim') : $t('Não') }}
          </b-badge>
        </template>

        <!-- Coluna: Alerta Permanência Dentro (Stay In) -->
        <template #cell(alertStayIn)="data">
          <b-badge :variant="data.item.alertStayIn ? 'light-success' : 'light-secondary'" class="font-small-1">
            <feather-icon :icon="data.item.alertStayIn ? 'CheckIcon' : 'XIcon'" size="10" class="mr-25" />
            {{ data.item.alertStayIn ? $t('Sim') : $t('Não') }}
          </b-badge>
        </template>

        <!-- Coluna: Alerta Permanência Fora (Stay Out) -->
        <template #cell(alertStayOut)="data">
          <b-badge :variant="data.item.alertStayOut ? 'light-success' : 'light-secondary'" class="font-small-1">
            <feather-icon :icon="data.item.alertStayOut ? 'CheckIcon' : 'XIcon'" size="10" class="mr-25" />
            {{ data.item.alertStayOut ? $t('Sim') : $t('Não') }}
          </b-badge>
        </template>

        <!-- Coluna: Ações -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center" style="gap: 4px;">
            <b-button
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              @click="focusAndSwitchToMap(data.item)"
              v-b-tooltip.hover
              :title="$t('Visualizar no Mapa')"
            >
              <feather-icon icon="MapPinIcon" size="14" />
            </b-button>
            <b-button
              variant="flat-info"
              size="sm"
              class="btn-icon"
              @click="editFence(data.item.id)"
              v-b-tooltip.hover
              :title="$t('Edit')"
            >
              <feather-icon icon="Edit2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="confirmDelete(data.item.id)"
              v-b-tooltip.hover
              :title="$t('Delete')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginação da Tabela -->
      <div class="p-1 d-flex flex-column flex-sm-row justify-content-between align-items-center border-top">
        <span class="text-muted font-small-3 mb-1 mb-sm-0">
          {{ $t('Showing') }} {{ (currentPage - 1) * perPage + (allFences.length ? 1 : 0) }}
          {{ $t('to') }} {{ Math.min(currentPage * perPage, allFences.length) }}
          {{ $t('of') }} {{ allFences.length }} {{ $t('entries') }}
        </span>

        <b-pagination
          v-model="currentPage"
          :total-rows="allFences.length"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="16" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="16" />
          </template>
        </b-pagination>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import { resolveIcon } from '@/utils/assetUtils'
import { useToast } from 'vue-toastification/composition'

import 'leaflet/dist/leaflet.css'
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LPolygon,
  LCircle,
  LControlLayers,
} from 'vue2-leaflet'
import MarkerCluster from 'vue2-leaflet-markercluster'

import {
  BCard,
  BCardBody,
  BCardHeader,
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BInputGroup,
  BInputGroupAppend,
  BFormInput,
  BFormSelect,
  BBadge,
  BSpinner,
  BTable,
  BPagination,
  VBTooltip,
} from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'

import PopupFence from '@/views/main/components/PopupFence.vue'
import customerStoreModule from '@/views/main/customer/customerStoreModule'
import assetStoreModule from '@/views/main/asset/assetStoreModule'
import fenceStoreModule from './fenceStoreModule'

export default {
  name: 'FenceManagement',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LPolygon,
    LCircle,
    LControlLayers,
    MarkerCluster,
    PopupFence,
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BFormSelect,
    BBadge,
    BSpinner,
    BTable,
    BPagination,
    VueAutosuggest,
  },
  setup(props, { root }) {
    const toast = useToast()

    const FENCE_APP_STORE_MODULE_NAME = 'app-fence'
    const CUSTOMER_APP_STORE_MODULE_NAME = 'app-customer'
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'

    if (!store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
      store.registerModule(FENCE_APP_STORE_MODULE_NAME, fenceStoreModule)
    }
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) {
      store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule)
    }
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(FENCE_APP_STORE_MODULE_NAME)
      }
    })

    // Estado da visualização (mapa ou tabela)
    const viewMode = ref('map')
    const showFilters = ref(false)
    const showSidebar = ref(true)

    // Filtros de busca
    const searchQueryFence = ref('')
    const searchQueryCustomer = ref('')
    const selectedCustomerId = ref('')
    const includeSubcustomers = ref(false)

    // Paginação
    const currentPage = ref(1)
    const perPage = ref(20)
    const perPageOptions = [10, 20, 50, 100]
    const sidebarCurrentPage = ref(1)
    const sidebarPerPage = ref(20)

    // Estado dos dados (todas as cercas em memória)
    const allFences = ref([])
    const fencesList = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      return allFences.value.slice(start, start + perPage.value)
    })
    const totalFencesCount = computed(() => allFences.value.length)
    const detailedFencesMap = ref(new Map())
    const circleFences = ref([])
    const polygonFences = ref([])
    const isLoadingFences = ref(false)
    const selectedFenceId = ref(null)

    // Autosuggest de cliente
    const filteredOptionsCustomer = ref([])
    const inputPropsCustomer = computed(() => ({
      id: 'autosuggest-customer-input',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Buscar cliente...'),
    }))

    // Localização & Mapa
    const mapfence = ref(null)
    const center = ref([-19.801187, -43.948885])
    const zoom = ref(6)
    const locationQuery = ref('')
    const isSearchingLocation = ref(false)

    // URLs das camadas de mapas
    const arcgisSatelliteUrl = 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const arcgisLabelsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
    const arcgisRoadsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

    // Veículos / Ativos no mapa (ocultos por padrão)
    const showAssetsOnMap = ref(false)
    const isLoadingAssets = ref(false)
    const assets = ref([])
    const iconSize = ref(30)
    const selectedIcon = ref(true)

    const dynamicSize = computed(() => [iconSize.value, iconSize.value * 1.15])
    const dynamicAnchor = computed(() => (selectedIcon.value ? [27, 30] : [15, 13]))
    const dynamicTooltip = computed(() => (selectedIcon.value ? [25, 0] : [7, -15]))
    const dynamicPopup = computed(() => (selectedIcon.value ? [2, -27] : [-7, -13]))

    // Filtra apenas ativos com coordenadas válidas para evitar falhas no Leaflet MarkerCluster
    const assetsWithCoordinates = computed(() => (
      assets.value.filter(
        asset => Boolean(
          asset
          && asset.lastLat != null
          && asset.lastLng != null
          && asset.lastLat !== 0
          && asset.lastLng !== 0
          && !Number.isNaN(Number(asset.lastLat))
          && !Number.isNaN(Number(asset.lastLng)),
        ),
      )
    ))

    // Campos da tabela
    const tableFields = computed(() => [
      { key: 'name', label: root.$t('Nome'), sortable: true },
      { key: 'virtualFenceTypeName', label: root.$t('Tipo'), sortable: true },
      { key: 'alertIn', label: root.$t('Alerta Entrada'), sortable: true },
      { key: 'alertOut', label: root.$t('Alerta Saída'), sortable: true },
      { key: 'alertStayIn', label: root.$t('Permanece Dentro'), sortable: true },
      { key: 'alertStayOut', label: root.$t('Permanece Fora'), sortable: true },
      {
        key: 'actions',
        label: root.$t('Ações'),
        sortable: false,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
    ])

    // Filtro ativo
    const hasActiveFilters = computed(() => (
      Boolean(
        (searchQueryFence.value && searchQueryFence.value.trim() !== '')
        || (searchQueryCustomer.value && searchQueryCustomer.value.trim() !== '')
        || selectedCustomerId.value !== ''
        || includeSubcustomers.value === false,
      )
    ))

    // Métricas para os Cards de KPI
    const alertInCount = computed(() => allFences.value.filter(f => f.alertIn).length)
    const alertOutCount = computed(() => allFences.value.filter(f => f.alertOut).length)
    const alertStayCount = computed(() => allFences.value.filter(f => f.alertStayIn || f.alertStayOut).length)

    // Paginação local da lista da gaveta lateral (Map View) sem novo request
    const paginatedSidebarFences = computed(() => {
      const start = (sidebarCurrentPage.value - 1) * sidebarPerPage.value
      const end = start + sidebarPerPage.value
      return allFences.value.slice(start, end)
    })

    // Autosuggest handlers
    const selectHandlerCustomer = option => {
      if (option && option.item) {
        selectedCustomerId.value = option.item.id
        searchQueryCustomer.value = option.item.name
        filteredOptionsCustomer.value = []
      }
    }

    const onInputChangeCustomer = text => {
      if (!text || text.length < 2) {
        selectedCustomerId.value = ''
        filteredOptionsCustomer.value = []
        return
      }

      store
        .dispatch(`${CUSTOMER_APP_STORE_MODULE_NAME}/fetchCustomers`, {
          q: text,
          page: 1,
          perPage: 6,
        })
        .then(response => {
          filteredOptionsCustomer.value = [{ data: response.data.data || [] }]
        })
        .catch(() => {
          filteredOptionsCustomer.value = []
        })
    }

    let fetchSeq = 0

    const organizeFenceGeometry = fence => {
      const geometryId = fence.virtualFenceGeometryId || fence.geometryId
      if (geometryId === 1) {
        // Círculo
        if (!circleFences.value.some(c => c.id === fence.id)) {
          circleFences.value.push({
            ...fence,
            lat: Number(fence.lat) || 0,
            lng: Number(fence.lng) || 0,
            radius: Number(fence.radius) || 0,
          })
        }
      } else if (geometryId === 2) {
        // Polígono
        if (!polygonFences.value.some(p => p.id === fence.id)) {
          let formattedPoints = []
          if (Array.isArray(fence.points)) {
            const sorted = [...fence.points].sort((a, b) => ((a.order || 0) > (b.order || 0) ? 1 : -1))
            formattedPoints = sorted.map(pt => (Array.isArray(pt) ? pt : [pt.lat, pt.lng]))
          }
          polygonFences.value.push({
            ...fence,
            points: formattedPoints,
          })
        }
      }
    }

    // Ajusta o zoom do mapa para abranger todas as cercas
    const fitAllFences = () => {
      if (!mapfence.value || !mapfence.value.mapObject) return

      let minLat = Infinity
      let maxLat = -Infinity
      let minLng = Infinity
      let maxLng = -Infinity
      let hasPoints = false

      const updateBounds = (lat, lng) => {
        const numLat = Number(lat)
        const numLng = Number(lng)
        if (!Number.isNaN(numLat) && !Number.isNaN(numLng) && numLat !== 0 && numLng !== 0) {
          if (numLat < minLat) minLat = numLat
          if (numLat > maxLat) maxLat = numLat
          if (numLng < minLng) minLng = numLng
          if (numLng > maxLng) maxLng = numLng
          hasPoints = true
        }
      }

      circleFences.value.forEach(c => {
        if (c.lat && c.lng) updateBounds(c.lat, c.lng)
      })

      polygonFences.value.forEach(p => {
        if (Array.isArray(p.points)) {
          p.points.forEach(pt => {
            if (Array.isArray(pt)) updateBounds(pt[0], pt[1])
            else if (pt && pt.lat && pt.lng) updateBounds(pt.lat, pt.lng)
          })
        }
      })

      if (hasPoints) {
        mapfence.value.mapObject.fitBounds(
          [
            [minLat, minLng],
            [maxLat, maxLng],
          ],
          { padding: [40, 40], maxZoom: 15 },
        )
      }
    }

    // Carrega geometrias detalhadas de cada cerca para desenhar todas no mapa
    const loadGeometriesForFences = async (items, seq) => {
      circleFences.value = []
      polygonFences.value = []

      const itemsToFetch = []

      // 1. Organiza imediatamente itens que já possuem geometria ou estão no cache
      items.forEach(item => {
        if (detailedFencesMap.value.has(item.id)) {
          organizeFenceGeometry(detailedFencesMap.value.get(item.id))
        } else if (
          item.virtualFenceGeometryId
          || item.geometryId
          || (item.lat && item.lng)
          || (item.points && item.points.length)
        ) {
          const detailed = {
            ...item,
            virtualFenceGeometryId: item.virtualFenceGeometryId || item.geometryId,
          }
          detailedFencesMap.value.set(item.id, detailed)
          organizeFenceGeometry(detailed)
        } else {
          itemsToFetch.push(item)
        }
      })

      // 2. Se houver itens sem geometria no payload da listagem, busca os detalhes em lotes concorrentes
      if (itemsToFetch.length > 0) {
        const fetchBatch = async batch => {
          await Promise.all(
            batch.map(async item => {
              try {
                const res = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFence`, { id: item.id })
                if (res && res.data) {
                  detailedFencesMap.value.set(item.id, res.data)
                  if (!seq || seq === fetchSeq) {
                    organizeFenceGeometry(res.data)
                  }
                }
              } catch (_) {
                // silencia falha individual mantendo as demais cercas
              }
            }),
          )
        }

        const BATCH_SIZE = 25
        for (let i = 0; i < itemsToFetch.length; i += BATCH_SIZE) {
          if (seq && seq !== fetchSeq) return
          const batch = itemsToFetch.slice(i, i + BATCH_SIZE)
          // eslint-disable-next-line no-await-in-loop
          await fetchBatch(batch)
        }
      }

      if (items.length > 0 && (!seq || seq === fetchSeq)) {
        nextTick(() => {
          fitAllFences()
        })
      }
    }

    // Foca em uma cerca específica ao clicar nela na sidebar ou tabela
    const focusFence = async fence => {
      selectedFenceId.value = fence.id

      let detailed = detailedFencesMap.value.get(fence.id)
      if (!detailed) {
        if (
          fence.virtualFenceGeometryId
          || fence.geometryId
          || (fence.lat && fence.lng)
          || (fence.points && fence.points.length)
        ) {
          detailed = {
            ...fence,
            virtualFenceGeometryId: fence.virtualFenceGeometryId || fence.geometryId,
          }
          detailedFencesMap.value.set(fence.id, detailed)
          organizeFenceGeometry(detailed)
        } else {
          try {
            const res = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFence`, { id: fence.id })
            detailed = res.data
            detailedFencesMap.value.set(fence.id, detailed)
            organizeFenceGeometry(detailed)
          } catch (_) {
            return
          }
        }
      }

      if (!mapfence.value || !mapfence.value.mapObject) return

      const geometryId = detailed.virtualFenceGeometryId || detailed.geometryId
      if (geometryId === 1 && detailed.lat && detailed.lng) {
        mapfence.value.mapObject.flyTo([detailed.lat, detailed.lng], 15, { duration: 1 })
      } else if (geometryId === 2 && Array.isArray(detailed.points) && detailed.points.length > 0) {
        const bounds = detailed.points.map(pt => (Array.isArray(pt) ? pt : [pt.lat, pt.lng]))
        mapfence.value.mapObject.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 })
      }
    }

    // Busca principal de cercas: carrega todas as cercas de uma vez e guarda em memória
    const fetchFencesList = async () => {
      fetchSeq += 1
      const currentSeq = fetchSeq
      isLoadingFences.value = true
      currentPage.value = 1
      sidebarCurrentPage.value = 1

      try {
        let page = 1
        const perBatch = 5000
        const allItems = []
        let hasMore = true

        while (hasMore) {
          if (currentSeq !== fetchSeq) return

          // eslint-disable-next-line no-await-in-loop
          const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFences`, {
            search: searchQueryFence.value,
            customerId: selectedCustomerId.value,
            includeSubcustomers: includeSubcustomers.value,
            page,
            perPage: perBatch,
          })

          if (currentSeq !== fetchSeq) return

          const pageData = response?.data?.data || []
          const total = response?.data?.totalItems

          allItems.push(...pageData)

          if (!pageData.length || (total != null && allItems.length >= total) || pageData.length < perBatch) {
            hasMore = false
          } else {
            page += 1
          }
        }

        if (currentSeq !== fetchSeq) return

        allFences.value = allItems

        // Carrega os detalhes geométricos de todas as cercas para desenhar todas no mapa
        await loadGeometriesForFences(allItems, currentSeq)
      } catch (error) {
        if (currentSeq === fetchSeq) {
          toast.error(root.$t('Erro ao carregar lista de cercas virtuais.'))
        }
      } finally {
        if (currentSeq === fetchSeq) {
          isLoadingFences.value = false
        }
      }
    }

    const focusAndSwitchToMap = fence => {
      viewMode.value = 'map'
      nextTick(() => {
        focusFence(fence)
      })
    }

    // O botão e gatilhos de busca realizam uma nova requisição à API
    const handleSearch = () => {
      fetchFencesList()
    }

    const clearFilters = () => {
      searchQueryFence.value = ''
      searchQueryCustomer.value = ''
      selectedCustomerId.value = ''
      includeSubcustomers.value = false
      filteredOptionsCustomer.value = []
      handleSearch()
    }

    const onPageChange = page => {
      currentPage.value = page
    }

    // Navegação para criação e edição
    const newVirtualFence = () => {
      let queryCriterion = {}
      if (mapfence.value && mapfence.value.mapObject) {
        const centerPoint = mapfence.value.mapObject.getCenter()
        const currentZoom = mapfence.value.mapObject.getZoom()
        queryCriterion = {
          zoom: currentZoom,
          lat: centerPoint.lat,
          lng: centerPoint.lng,
        }
      }
      router.push({
        name: 'fence-register',
        query: { criterion: queryCriterion },
      })
    }

    const editFence = id => {
      router.push({
        name: 'fence-register',
        params: { id },
      })
    }

    // Exclusão com confirmação SweetAlert2
    const confirmDelete = id => {
      root
        .$swal({
          title: root.$t('Are you sure?'),
          text: root.$t("You can't revert your action"),
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: root.$t('Yes'),
          cancelButtonText: root.$t('No'),
          showCloseButton: true,
          showLoaderOnConfirm: true,
          customClass: {
            cancelButton: 'btn btn-outline-secondary',
            confirmButton: 'btn btn-danger mr-1',
          },
          buttonsStyling: false,
        })
        .then(async result => {
          if (result.value) {
            try {
              await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/deleteFence`, { id })
              root.$swal(root.$t('Deleted'), root.$t('You successfully deleted'), 'success')

              // Remove localmente
              allFences.value = allFences.value.filter(f => f.id !== id)
              circleFences.value = circleFences.value.filter(c => c.id !== id)
              polygonFences.value = polygonFences.value.filter(p => p.id !== id)
              detailedFencesMap.value.delete(id)
            } catch (error) {
              root.$swal(root.$t('Error'), root.$t('Falha ao excluir a cerca virtual.'), 'error')
            }
          }
        })
    }

    // Busca de endereço ou coordenadas
    const searchLocation = async () => {
      if (!locationQuery.value || locationQuery.value.trim() === '') {
        toast.warning(root.$t('Please enter an address or coordinates'))
        return
      }

      isSearchingLocation.value = true

      // Verifica se é coordenada direta (lat,lng)
      const coordRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/
      if (coordRegex.test(locationQuery.value.trim())) {
        const [lat, lng] = locationQuery.value.split(',').map(Number)
        if (mapfence.value && mapfence.value.mapObject) {
          mapfence.value.mapObject.flyTo([lat, lng], 15)
        }
        isSearchingLocation.value = false
        return
      }

      // Geocodificação Nominatim
      try {
        const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchAddres`, {
          address: locationQuery.value.trim(),
        })

        if (!response.data || response.data.length === 0) {
          toast.error(root.$t('Address not found'))
          return
        }

        const firstResult = response.data[0]
        const lat = parseFloat(firstResult.lat)
        const lon = parseFloat(firstResult.lon)

        if (mapfence.value && mapfence.value.mapObject) {
          mapfence.value.mapObject.flyTo([lat, lon], 14, { duration: 1.2 })
        }
        toast.success(root.$t('Localização encontrada'))
      } catch (e) {
        toast.error(root.$t('Erro ao buscar localização'))
      } finally {
        isSearchingLocation.value = false
      }
    }

    let fetchAssetsRequestId = 0

    // Carrega ativos/veículos com paginação recursiva sob demanda explícita do usuário
    const fetchAssets = (page = 1, currentAssets = []) => {
      // Se a camada estiver desligada pelo usuário, não dispara requisição
      if (!showAssetsOnMap.value) {
        isLoadingAssets.value = false
        return
      }

      if (page === 1) {
        fetchAssetsRequestId += 1
        isLoadingAssets.value = true
      }
      const currentRequestId = fetchAssetsRequestId

      store
        .dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssets`, {
          q: '',
          perPage: 300,
          page,
          sortBy: 'workstatus',
          sortDesc: false,
          status: null,
          customerId: selectedCustomerId.value || '',
          workStatusList: [],
        })
        .then(response => {
          // Se o usuário ocultou a camada enquanto carregava ou disparou outra busca
          if (!showAssetsOnMap.value || currentRequestId !== fetchAssetsRequestId) {
            isLoadingAssets.value = false
            return
          }

          const data = (response && response.data && response.data.data) || []
          const accumulated = [...currentAssets, ...data]
          if (
            response
            && response.data
            && response.data.totalItems
            && page < Math.ceil(response.data.totalItems / 300)
          ) {
            fetchAssets(page + 1, accumulated)
          } else {
            assets.value = accumulated
            isLoadingAssets.value = false

            // Feedback informativo imediato (regras de ouro do design)
            if (accumulated.length === 0) {
              toast.info(root.$t('Nenhum veículo encontrado para este cliente.'))
            } else {
              const withCoords = accumulated.filter(
                a => a
                  && a.lastLat != null
                  && a.lastLng != null
                  && a.lastLat !== 0
                  && a.lastLng !== 0,
              )
              if (withCoords.length === 0) {
                toast.warning(root.$t('Veículos encontrados, mas nenhum possui coordenadas GPS válidas no momento.'))
              }
            }
          }
        })
        .catch(() => {
          if (currentRequestId === fetchAssetsRequestId) {
            isLoadingAssets.value = false
            toast.error(root.$t('Erro ao carregar veículos no mapa.'))
          }
        })
    }

    const toggleShowAssets = () => {
      showAssetsOnMap.value = !showAssetsOnMap.value
      if (showAssetsOnMap.value) {
        // Usuário escolheu mostrar: busca os ativos se ainda não foram carregados
        if (assets.value.length === 0) {
          fetchAssets(1)
        }
      } else {
        // Usuário escolheu ocultar: encerra indicador de carregamento
        isLoadingAssets.value = false
      }
    }

    const refreshAssets = () => {
      if (!isLoadingAssets.value && showAssetsOnMap.value) {
        fetchAssets(1)
      }
    }

    watch(selectedCustomerId, () => {
      if (showAssetsOnMap.value) {
        fetchAssets(1)
      } else {
        assets.value = []
      }
    })

    onMounted(() => {
      fetchFencesList()
    })

    return {
      viewMode,
      showFilters,
      showSidebar,
      searchQueryFence,
      searchQueryCustomer,
      selectedCustomerId,
      includeSubcustomers,
      currentPage,
      perPage,
      perPageOptions,
      sidebarCurrentPage,
      sidebarPerPage,
      totalFencesCount,
      allFences,
      fencesList,
      paginatedSidebarFences,
      circleFences,
      polygonFences,
      isLoadingFences,
      selectedFenceId,
      filteredOptionsCustomer,
      inputPropsCustomer,
      tableFields,
      hasActiveFilters,
      alertInCount,
      alertOutCount,
      alertStayCount,
      mapfence,
      center,
      zoom,
      locationQuery,
      isSearchingLocation,
      arcgisSatelliteUrl,
      arcgisLabelsUrl,
      arcgisRoadsUrl,
      showAssetsOnMap,
      isLoadingAssets,
      toggleShowAssets,
      refreshAssets,
      assets,
      assetsWithCoordinates,
      iconSize,
      selectedIcon,
      dynamicSize,
      dynamicAnchor,
      dynamicTooltip,
      dynamicPopup,
      resolveIcon,
      selectHandlerCustomer,
      onInputChangeCustomer,
      fetchFencesList,
      fitAllFences,
      focusFence,
      focusAndSwitchToMap,
      handleSearch,
      clearFilters,
      onPageChange,
      newVirtualFence,
      editFence,
      confirmDelete,
      searchLocation,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/map-leaflet.scss";
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

#map-fence .someExtraClass {
  padding: 4px;
  border-radius: 50px;
  box-shadow: 5px 3px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  width: auto !important;
  height: auto !important;
}

#map-fence .asset-on {
  background-color: #28c76f !important;
}

#map-fence .asset-off {
  background-color: #82868b !important;
}

#map-fence .asset-failure {
  background-color: #ef5258 !important;
}

#map-fence .asset-noconnection {
  background-color: #f5f4f8 !important;
}

#map-fence .asset-severe {
  background-color: #ff9d54 !important;
}

#map-fence .leaflet-marker-icon.asset-iddle {
  background-color: #00cfe8 !important;
}

#map-fence .marker-no-icon {
  background-color: red !important;
}

#map-fence .with-icon {
  width: 3rem;
  height: auto;
}
</style>

<style lang="scss" scoped>
.fence-management-page {
  position: relative;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}

.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;

  &--primary {
    background-color: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }
  &--success {
    background-color: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }
  &--warning {
    background-color: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
  }
  &--info {
    background-color: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }
}

/* Painel de Filtros */
.filters-card {
  border-radius: 8px;
  border: 1px solid rgba(115, 103, 240, 0.15);
}

/* Wrapper do Mapa e Toolbar Flutuante */
.map-view-wrapper {
  position: relative;
  width: 100%;
  height: calc(100vh - 240px);
  min-height: 520px;
}

.map-container {
  width: 100%;
  height: 100%;
}

.map-canvas {
  width: 100%;
  height: 100%;
}

.map-floating-toolbar {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 60px;
  z-index: 999;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  gap: 8px;

  > div {
    pointer-events: auto;
  }
}

.location-search-box {
  width: 280px;
  max-width: 100%;
  background: white;
  border-radius: 6px;
  overflow: hidden;
}

/* Controle de Camada de Ativos no Mapa */
.asset-layer-control {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease;

  .btn {
    border: none;
    font-size: 0.85rem;
    transition: all 0.2s ease;
  }

  .border-left-light {
    border-left: 1px solid rgba(255, 255, 255, 0.2) !important;
  }

  .badge-asset-count {
    background-color: rgba(255, 255, 255, 0.28) !important;
    color: #ffffff !important;
    font-weight: 600;
  }

  .badge-asset-off {
    background-color: rgba(0, 0, 0, 0.08) !important;
    color: #6e6b7b !important;
    font-weight: 500;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.feather-spin {
  animation: spin 1s linear infinite;
}

/* Sidebar Gaveta Flutuante de Cercas */
.fence-sidebar-drawer {
  position: absolute;
  top: 54px;
  left: 12px;
  bottom: 12px;
  width: 320px;
  max-width: calc(100% - 24px);
  background: #ffffff;
  border-radius: 8px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
}

.fence-item-card {
  background: #f8f8fa;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    background: #f0f0f5;
  }

  &--active {
    background: rgba(115, 103, 240, 0.08);
    border-color: #7367f0;
  }
}

.fence-card-title {
  max-width: 170px;
}

.fence-alert-chip {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;

  &--active {
    background-color: rgba(40, 199, 111, 0.15);
    color: #28c76f;
  }

  &--inactive {
    background-color: #ededf2;
    color: #b0b2c0;
  }
}

/* Tabela */
.fence-table-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: rgba(115, 103, 240, 0.1);
}

/* Transições suaves */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}
.slide-right-enter,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-15px);
}
</style>
